import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  limit,
  orderBy,
  startAt,
  endAt,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../config/firebase";
import {
  getDatabase,
  ref as databaseRef,
  get as databaseGet,
  set as databaseSet,
} from "firebase/database";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { captureFirebaseError } from "../config/sentry";

// Define the URL for creating custom tokens
const functionUrl = `https://us-central1-supporty-staging.cloudfunctions.net/createCustomToken`;

// Interface for User data
interface User {
  uid: string;
  name: string;
  email: string;
  role: string;
  uiRole?: string;
  profileImageUrl?: string;
  orgId?: string;
  organizations?: any;
  specialities?: any;
  ratePerMinute?: string;
  bio?: string;
  amount: number;
  company?: string;
}

// Interface for Activity data
interface Activity {
  userId: string;
  userName: string;
  activityId: string;
  timestamp?: { seconds: number };
  [key: string]: any;
}

// Interface for notificationSettings
interface NotificationSettings {
  email?: boolean;
  SMS?: boolean;
  phoneNumber?: string;
  [key: string]: any;
}

// Interface for SignupData
interface SignupData {
  name: string;
  email: string;
  password: string;
  company?: string;
  role: "Customer" | "Expert" | "supporty_admin";
  specialities?: any;
  ratePerMinute?: string;
  bio?: string;
  otherSpecify?: string;
}

// Define the initial state
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  fetchedUsers: User[];
  fetchLoading: boolean;
  fetchError: string;
  activities: any[];
  allActivities: Activity[];
  allAcitivityWithoutRole: any[];
  notificationSettings: NotificationSettings;
  lastVisibleDoc: any;
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem("user") || "null"),
  isAuthenticated: localStorage.getItem("isAuthenticated") === "true",
  fetchedUsers: [],
  fetchLoading: false,
  fetchError: "",
  activities: [],
  allActivities: [],
  allAcitivityWithoutRole: [],
  notificationSettings: {},
  lastVisibleDoc: null,
};

// Helper function to track Firebase operations with Sentry
const withSentryTransaction = async <T>(
  name: string,
  operation: string,
  func: () => Promise<T>
): Promise<T> => {
  const transaction = Sentry.startTransaction({
    name: `auth.${name}`,
    op: operation,
  });

  Sentry.configureScope((scope) => {
    scope.setSpan(transaction);
  });

  try {
    return await func();
  } catch (error) {
    captureFirebaseError(error as Error, {
      operation: name,
      transaction: transaction.toJSON(),
    });
    throw error;
  } finally {
    transaction.finish();
  }
};

// Login thunk
export const login = createAsyncThunk(
  "auth/login",
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      return await withSentryTransaction("login", "auth", async () => {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
        const firebaseUser = userCredential.user;

        // Fetch user profile from Firestore
        const userDocRef = doc(db, `users/${firebaseUser.uid}`);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const user: User = {
            uid: firebaseUser.uid,
            name: userData.name,
            email: userData.email,
            role: userData.role,
            uiRole: userData.uiRole,
            profileImageUrl: userData.profileImageUrl,
            orgId: userData.orgId,
            organizations: userData.organizations || [],
            amount: userData.amount || 0,
            ...(userData.specialities && {
              specialities: userData.specialities,
            }),
            ...(userData.ratePerMinute && {
              ratePerMinute: userData.ratePerMinute,
            }),
            ...(userData.bio && { bio: userData.bio }),
          };

          // Add user information to Sentry
          Sentry.setUser({
            uid: user.uid,
            email: user.email,
            username: user.name,
          });

          // Fetch notification settings from Realtime Database
          const settingsRef = databaseRef(
            getDatabase(),
            `users/${firebaseUser.uid}/setting`
          );
          const settingsSnapshot = await databaseGet(settingsRef);
          const notificationSettings = settingsSnapshot.exists()
            ? settingsSnapshot.val()
            : {};

          await logActivity(user.uid, "Sign In", { details: "sign in" });

          // Generate custom token and send to Chrome extension
          try {
            const response = await axios.post(functionUrl, {
              uid: firebaseUser.uid,
            });
            const customToken = response.data.customToken;

            if (typeof chrome !== "undefined" && chrome.runtime) {
              // Send message to extension 1
              chrome.runtime.sendMessage(
                "ndcmfkbjobahmdpjakmgnomklohgalfc",
                { type: "LOGIN", token: customToken, user },
                (response: { success: boolean; message: string }) => {
                  if (chrome.runtime.lastError) {
                    console.error(
                      "Chrome extension error:",
                      chrome.runtime.lastError.message
                    );
                  }
                }
              );

              // Send message to extension 2
              chrome.runtime.sendMessage(
                "hedjogelnmjoilmlomnekfbchmbfngja",
                { type: "LOGIN", token: customToken, user },
                (response: { success: boolean; message: string }) => {
                  if (chrome.runtime.lastError) {
                    console.error(
                      "Chrome extension error:",
                      chrome.runtime.lastError.message
                    );
                  }
                }
              );
            }
          } catch (error) {
            console.error("Failed to generate or send custom token:", error);
            Sentry.captureException(error);
          }

          if (notificationSettings.email || notificationSettings.SMS) {
            localStorage.setItem("isAuthenticated", "false");
            return {
              requiresOTP: true,
              notificationSettings,
              user,
            };
          } else {
            localStorage.setItem("isAuthenticated", "true");
            localStorage.setItem("user", JSON.stringify(user));
            return {
              requiresOTP: false,
              notificationSettings,
              user,
              isAuthenticated: true,
            };
          }
        } else {
          Sentry.captureMessage("User profile not found in Firestore", "error");
          throw new Error("User profile not found");
        }
      });
    } catch (error: any) {
      console.error("Login failed:", error);
      return rejectWithValue(error.message || "Invalid email or password");
    }
  }
);

// Sign up thunk
export const signup = createAsyncThunk(
  "auth/signup",
  async (data: SignupData, { rejectWithValue }) => {
    try {
      return await withSentryTransaction("signup", "auth", async () => {
        const {
          name,
          email,
          password,
          role,
          specialities,
          ratePerMinute,
          bio,
          company,
          otherSpecify,
        } = data;
        const uiRole = role === "Expert" ? "expert_owner" : "customer_owner";

        // Create the user in Firebase Authentication
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const firebaseUser = userCredential.user;

        // Store additional user data in Firestore
        // Filter out undefined values to prevent Firestore errors
        const userDoc = doc(db, "users", firebaseUser.uid);
        const userDataToStore: any = {
          uid: firebaseUser.uid, // Explicitly store the uid in the Firestore document
          name,
          email,
          role,
          uiRole,
          amount: 0,
          createdAt: new Date(),
        };

        // Only add company if it's defined (not undefined)
        if (company !== undefined) {
          userDataToStore.company = company;
        }

        // Only add Expert-specific fields if they exist and are not undefined
        if (role === "Expert") {
          if (specialities !== undefined)
            userDataToStore.specialities = specialities;
          if (ratePerMinute !== undefined)
            userDataToStore.ratePerMinute = ratePerMinute;
          if (bio !== undefined) userDataToStore.bio = bio;
          if (otherSpecify !== undefined)
            userDataToStore.otherSpecify = otherSpecify;
        }

        await setDoc(userDoc, userDataToStore);

        // Update the Zustand state
        const newUser = {
          uid: firebaseUser.uid,
          name,
          email,
          role,
          amount: 0,
        };

        // Add company to newUser only if defined
        if (company !== undefined) {
          newUser.company = company;
        }

        // Set user in Sentry
        Sentry.setUser({
          uid: firebaseUser.uid,
          email,
          username: name,
        });

        // Log activity
        await logActivity(firebaseUser.uid, "Sign Up", { details: "sign up" });

        // Generate custom token and send to Chrome extension
        try {
          const response = await axios.post(functionUrl, {
            uid: firebaseUser.uid,
          });
          const customToken = response.data.customToken;

          if (typeof chrome !== "undefined" && chrome.runtime) {
            chrome.runtime.sendMessage(
              "ndcmfkbjobahmdpjakmgnomklohgalfc",
              { type: "LOGIN", token: customToken, newUser },
              (response: { success: boolean; message: string }) => {
                if (chrome.runtime.lastError) {
                  console.error(
                    "Chrome extension error:",
                    chrome.runtime.lastError.message
                  );
                }
              }
            );

            chrome.runtime.sendMessage(
              "hedjogelnmjoilmlomnekfbchmbfngja",
              { type: "LOGIN", token: customToken, newUser },
              (response: { success: boolean; message: string }) => {
                if (chrome.runtime.lastError) {
                  console.error(
                    "Chrome extension error:",
                    chrome.runtime.lastError.message
                  );
                }
              }
            );
          }
        } catch (error) {
          console.error("Failed to generate or send custom token:", error);
          Sentry.captureException(error);
        }

        return {
          success: true,
          userId: firebaseUser.uid,
          message: "User signed up successfully.",
          user: newUser,
        };
      });
    } catch (error: any) {
      console.error("Signup failed:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Reset password thunk
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (email: string, { rejectWithValue }) => {
    try {
      return await withSentryTransaction("resetPassword", "auth", async () => {
        try {
          // Use Firebase Auth's sendPasswordResetEmail
          // This will throw an error if the email doesn't exist in Firebase Auth
          await sendPasswordResetEmail(auth, email);

          Sentry.addBreadcrumb({
            category: "auth",
            message: "Password reset email sent",
            level: "info",
            data: { email },
          });

          const user = auth.currentUser;
          if (user) {
            await logActivity(user.uid, "Reset Password", {
              details: "Password Reset Requested",
            });
          }

          return "Password reset email sent. Please check your inbox";
        } catch (error: any) {
          // Firebase will throw an error if the email doesn't exist
          console.error("Password reset error:", error);

          Sentry.addBreadcrumb({
            category: "auth",
            message: "Password reset failed - email not found",
            level: "warning",
            data: { email, error: error.message },
          });

          return rejectWithValue(
            "Email not found. Please register first before attempting to reset password."
          );
        }
      });
    } catch (error: any) {
      console.error("Password reset error:", error.message);
      return rejectWithValue(
        "Failed to send password reset email. Please try again."
      );
    }
  }
);

// Skip 2FA authentication
export const skip2FactorAuthentication = createAsyncThunk(
  "auth/skip2FA",
  async (user: User, { dispatch }) => {
    localStorage.setItem("isAuthenticated", "true");
    Sentry.addBreadcrumb({
      category: "auth",
      message: "2FA skipped",
      level: "info",
    });
    return user;
  }
);

// Verify OTP
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (otp: string, { getState, rejectWithValue }) => {
    return withSentryTransaction("verifyOtp", "auth", async () => {
      try {
        // This is a placeholder for the real OTP verification which would be done by your backend
        // In this example, we're assuming the OTP is valid if it's not empty
        const isValidOtp = otp && otp.length > 0;

        if (isValidOtp) {
          const firebaseUser = auth.currentUser;
          if (firebaseUser) {
            const userDocRef = doc(db, `users/${firebaseUser.uid}`);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const user: User = {
                uid: firebaseUser.uid,
                name: userData.name,
                email: userData.email,
                role: userData.role,
                teamRole: userData.teamRole,
                profileImageUrl: userData.profileImageUrl,
                orgId: userData.orgId,
                organizations: userData.organizations || [],
                amount: userData.amount || 0,
                ...(userData.specialities && {
                  specialities: userData.specialities,
                }),
                ...(userData.ratePerMinute && {
                  ratePerMinute: userData.ratePerMinute,
                }),
                ...(userData.bio && { bio: userData.bio }),
              };

              // Set user in Sentry
              Sentry.setUser({
                uid: user.uid,
                email: user.email,
                username: user.name,
              });

              localStorage.setItem("isAuthenticated", "true");
              localStorage.setItem("user", JSON.stringify(user));

              Sentry.addBreadcrumb({
                category: "auth",
                message: "OTP verified successfully",
                level: "info",
              });

              return user;
            }
          }
          return {
            success: true,
            message: "OTP verified successfully.",
          };
        } else {
          console.error("Invalid OTP.");
          Sentry.addBreadcrumb({
            category: "auth",
            message: "Invalid OTP entered",
            level: "error",
          });
          throw new Error("Invalid OTP");
        }
      } catch (error: any) {
        console.error("OTP verification failed:", error);
        return rejectWithValue(error.message);
      }
    });
  }
);

// Logout thunk
export const logout = createAsyncThunk("auth/logout", async () => {
  // Create a transaction with better naming for filtering
  const transaction = Sentry.startTransaction({
    name: "auth.logout",
    op: "auth",
  });

  // Add specific tags to make filtering easier
  transaction.setTag("event_type", "user_logout");
  transaction.setTag("auth_action", "logout");

  // Add user context before they're logged out (if available)
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  if (currentUser) {
    transaction.setContext("user_context", {
      user_id: currentUser.id,
      email: currentUser.email,
      role: currentUser.role,
      logout_time: new Date().toISOString(),
    });
  }

  Sentry.configureScope((scope) => {
    scope.setSpan(transaction);
    // Add custom context that will be attached to the transaction
    scope.setContext("logout_details", {
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      platform: navigator.platform,
    });
  });

  // Create a dedicated event for logout
  Sentry.captureMessage("User logged out", "info");

  try {
    await auth.signOut();

    // Enhanced breadcrumb with more details
    Sentry.addBreadcrumb({
      category: "auth",
      message: "User logged out successfully",
      level: "info",
      data: {
        timestamp: new Date().toISOString(),
        status: "success",
        user_id: currentUser?.id,
      },
    });

    // Clear user in Sentry before localStorage
    Sentry.setUser(null);

    // Clear localStorage items
    localStorage.removeItem("authUser");
    localStorage.removeItem("user");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("2Factor");

    // Communicate with Chrome extensions
    if (typeof chrome !== "undefined" && chrome.runtime) {
      chrome.runtime.sendMessage(
        "ndcmfkbjobahmdpjakmgnomklohgalfc",
        { type: "LOGOUT" },
        (response) => {
          if (chrome.runtime.lastError) {
            console.error(
              "Error sending logout message:",
              chrome.runtime.lastError.message
            );
          }
        }
      );

      chrome.runtime.sendMessage(
        "hedjogelnmjoilmlomnekfbchmbfngja",
        { type: "LOGOUT" },
        (response) => {
          if (chrome.runtime.lastError) {
            console.error(
              "Error sending logout message:",
              chrome.runtime.lastError.message
            );
          }
        }
      );
    }

    return true;
  } catch (error: any) {
    console.error("Logout failed:", error.message);

    // Enhanced error capture with more context
    captureFirebaseError(error, {
      operation: "logout",
      status: "failed",
      user_id: currentUser?.uid,
      timestamp: new Date().toISOString(),
    });

    // Still clear local storage on error
    localStorage.removeItem("user");
    localStorage.setItem("isAuthenticated", "false");

    throw error;
  } finally {
    transaction.finish();
  }
});

// Log activity function
export const logActivity = async (
  userId: string,
  type: string,
  metadata: object = {}
) => {
  try {
    const span = Sentry.startTransaction({
      name: "logActivity",
      op: "data",
    });
    Sentry.configureScope((scope) => scope.setSpan(span));

    const activitiesRef = collection(db, `users/${userId}/activities`);
    await addDoc(activitiesRef, {
      type,
      timestamp: new Date(),
      metadata,
    });

    // Add breadcrumb for activity
    Sentry.addBreadcrumb({
      category: "activity",
      message: `User activity: ${type}`,
      level: "info",
      data: { ...metadata, userId },
    });

    span.finish();
    return true;
  } catch (error) {
    console.error("Failed to log activity:", error);
    captureFirebaseError(error as Error, {
      operation: "logActivity",
      userId,
      activityType: type,
    });
    return false;
  }
};

// Fetch user activities thunk
export const fetchUserActivities = createAsyncThunk(
  "auth/fetchUserActivities",
  async (_, { rejectWithValue }) => {
    try {
      return await withSentryTransaction(
        "fetchUserActivities",
        "data",
        async () => {
          const firebaseUser = auth.currentUser;

          if (!firebaseUser) {
            Sentry.captureMessage(
              "No logged-in user found when fetching activities",
              "warning"
            );
            return [];
          }

          const activitiesCollection = collection(
            db,
            `users/${firebaseUser?.uid}/activities`
          );
          const activitiesSnapshot = await getDocs(activitiesCollection);
          const activitiesList = activitiesSnapshot.docs
            .map((doc) => doc.data())
            .sort((a, b) => {
              const timestampA = a.timestamp?.seconds || 0;
              const timestampB = b.timestamp?.seconds || 0;
              return timestampB - timestampA;
            });

          return activitiesList;
        }
      );
    } catch (error: any) {
      console.error("Failed to fetch activities:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Create the auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      if (action.payload) {
        localStorage.setItem("user", JSON.stringify(action.payload));
      }
    },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
      localStorage.setItem("isAuthenticated", action.payload.toString());
    },
    setError: (state, action: PayloadAction<string>) => {
      state.fetchError = action.payload;
    },
    clearError: (state) => {
      state.fetchError = "";
    },
  },
  extraReducers: (builder) => {
    // Login cases
    builder.addCase(login.pending, (state) => {
      state.fetchLoading = true;
      state.fetchError = "";
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.fetchLoading = false;
      if (!action.payload.requiresOTP) {
        state.user = action.payload.user;
        state.isAuthenticated = true;
      }
      state.notificationSettings = action.payload.notificationSettings;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.fetchLoading = false;
      state.fetchError = action.payload as string;
    });

    // Signup cases
    builder.addCase(signup.pending, (state) => {
      state.fetchLoading = true;
      state.fetchError = "";
    });
    builder.addCase(signup.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.user = action.payload.user;
      state.isAuthenticated = false; // User needs to complete 2FA
    });
    builder.addCase(signup.rejected, (state, action) => {
      state.fetchLoading = false;
      state.fetchError = action.payload as string;
    });

    // Reset password cases
    builder.addCase(resetPassword.pending, (state) => {
      state.fetchLoading = true;
      state.fetchError = "";
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.fetchLoading = false;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.fetchLoading = false;
      state.fetchError = action.payload as string;
    });

    // Skip 2FA cases
    builder.addCase(skip2FactorAuthentication.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    });

    // Verify OTP cases
    builder.addCase(verifyOtp.pending, (state) => {
      state.fetchLoading = true;
      state.fetchError = "";
    });
    builder.addCase(verifyOtp.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
    });
    builder.addCase(verifyOtp.rejected, (state, action) => {
      state.fetchLoading = false;
      state.fetchError = action.payload as string;
    });

    // Logout cases
    builder.addCase(logout.pending, (state) => {
      state.fetchLoading = true;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.fetchLoading = false;
      state.user = null;
      state.isAuthenticated = false;
      state.activities = [];
      state.notificationSettings = {};
    });
    builder.addCase(logout.rejected, (state) => {
      state.fetchLoading = false;
      state.user = null;
      state.isAuthenticated = false;
    });

    // Fetch activities cases
    builder.addCase(fetchUserActivities.pending, (state) => {
      state.fetchLoading = true;
      state.fetchError = "";
    });
    builder.addCase(fetchUserActivities.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.activities = action.payload;
    });
    builder.addCase(fetchUserActivities.rejected, (state, action) => {
      state.fetchLoading = false;
      state.fetchError = action.payload as string;
    });
  },
});

export const { setUser, setIsAuthenticated, setError, clearError } =
  authSlice.actions;

export default authSlice.reducer;

// Listen to auth state changes
onAuthStateChanged(auth, async (firebaseUser) => {
  try {
    if (firebaseUser) {
      // Fetch user data from users collection
      const userDocRef = doc(db, `users/${firebaseUser.uid}`);
      const userDoc = await getDoc(userDocRef);

      // Query profiles collection for matching userId
      const profilesRef = collection(db, "profiles");
      const q = query(profilesRef, where("userId", "==", firebaseUser.uid));
      const profileSnapshot = await getDocs(q);

      let profileData: any = {};
      if (!profileSnapshot.empty) {
        profileData = profileSnapshot.docs[0].data();
      }

      if (userDoc.exists() || !profileSnapshot.empty) {
        const userData = userDoc.exists() ? userDoc.data() : {};

        // Merge user data with profile data, profile data takes precedence
        const user: any = {
          id: firebaseUser.uid,
          name: profileData.name ,
          email: firebaseUser.email,
          role: profileData.role || userData.role,
          profileImageUrl:
            profileData.profileImageUrl || userData.profileImageUrl,
          orgId: userData.orgId || profileData.orgId,
          uid: firebaseUser.uid,
          teamRole: userData.teamRole,
          ...(profileData.specialities
            ? { specialities: profileData.specialities || userData.specialities }
            : {}),
          ...(profileData.ratePerMinute || userData.ratePerMinute
            ? { ratePerMinute: profileData.ratePerMinute || userData.ratePerMinute }
            : {}),
          ...(profileData.bio || userData.bio
            ? { bio: profileData.bio || userData.bio }
            : {}),
        };

        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("isAuthenticated", "true");


        Sentry.setUser({
          uid: user.uid,
          email: user.email,
          username: user.name,
        });

        Sentry.addBreadcrumb({
          category: "auth",
          message: "User authenticated",
          level: "info",
        });

        // Generate custom token and send to Chrome extensions
        try {
          const response = await axios.post(functionUrl, {
            uid: firebaseUser.uid,
          });
          const customToken = response.data.customToken;

          if (typeof chrome !== "undefined" && chrome.runtime) {
            chrome.runtime.sendMessage(
              "ndcmfkbjobahmdpjakmgnomklohgalfc",
              { type: "LOGIN", token: customToken, user },
              (response: any) => {
                if (chrome.runtime.lastError) {
                  console.error(
                    "Chrome extension error:",
                    chrome.runtime.lastError.message
                  );
                }
              }
            );

            chrome.runtime.sendMessage(
              "hedjogelnmjoilmlomnekfbchmbfngja",
              { type: "LOGIN", token: customToken, user },
              (response: any) => {
                if (chrome.runtime.lastError) {
                  console.error(
                    "Chrome extension error:",
                    chrome.runtime.lastError.message
                  );
                }
              }
            );
          }
        } catch (error) {
          console.error("Failed to generate or send custom token:", error);
          Sentry.captureException(error);
        }
      } else {
        console.error("User profile not found in Firestore.");
        Sentry.captureMessage("User profile not found in Firestore", "error");
      }
    } else {
      Sentry.setUser(null);
      localStorage.removeItem("user");
      localStorage.setItem("isAuthenticated", "false");
    }
  } catch (error) {
    console.error("Error in onAuthStateChanged listener:", error);
    captureFirebaseError(error as Error, { operation: "onAuthStateChanged" });
  }
});
